import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Calculator, ArrowRight, Sparkles, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { allTools, getSlug } from "../../data/allTools";

interface SearchBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onSelectToolCategory?: (category: string) => void;
}

export const HeroSearchBar: React.FC<SearchBarProps> = ({
  searchQuery,
  setSearchQuery,
  onSelectToolCategory,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Suggested popular calculators when search is empty/focused
  const featuredSuggestions = [
    { name: "Mortgage Calculator", category: "loans", desc: "Calculate monthly payments & amortization" },
    { name: "Compound Interest Calculator", category: "savings", desc: "Project growth with monthly additions" },
    { name: "Loan Calculator", category: "loans", desc: "Estimate interest & monthly paydown" },
    { name: "Debt Payoff Calculator", category: "loans", desc: "Snowball & avalanche payoff plans" },
    { name: "Retirement Calculator", category: "retirement", desc: "Project 401k & FIRE nest egg" },
    { name: "Income Tax Calculator", category: "tax", desc: "Federal brackets & take-home pay" },
    { name: "Budget Planner", category: "budget", desc: "50/30/20 monthly expense allocation" },
    { name: "Inflation Calculator", category: "savings", desc: "Future purchasing power erosion" },
  ];

  // Filter tools based on query
  const filteredTools = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return allTools
      .filter(
        (tool) =>
          tool.name.toLowerCase().includes(q) ||
          tool.description.toLowerCase().includes(q) ||
          tool.category.toLowerCase().includes(q)
      )
      .slice(0, 7);
  }, [searchQuery]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectTool = (toolName: string, category: string) => {
    setIsOpen(false);
    if (onSelectToolCategory) {
      onSelectToolCategory(category);
    }
    const slug = getSlug(toolName);
    navigate(`/${slug}`);
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-2xl z-30">
      {/* Search Input Container with Glow Border */}
      <motion.div
        whileFocus={{ scale: 1.01 }}
        className="relative flex items-center group"
      >
        {/* Soft Gold Glow effect on focus/hover */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500/30 via-amber-400/20 to-amber-600/30 rounded-2xl blur-md opacity-40 group-hover:opacity-80 transition duration-300 pointer-events-none" />

        <div className="relative w-full flex items-center bg-[#070d1d]/90 backdrop-blur-xl border border-white/10 group-focus-within:border-amber-500/60 rounded-2xl shadow-2xl transition-all duration-300">
          <div className="pl-4.5 pr-2 text-slate-400 group-focus-within:text-amber-400 transition-colors">
            <Search className="w-5 h-5" />
          </div>

          <input
            type="text"
            placeholder="Search calculators..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            className="w-full bg-transparent py-4 pl-1 pr-10 text-white placeholder-slate-400 text-base sm:text-lg outline-none font-normal"
          />

          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                setIsOpen(true);
              }}
              className="mr-3 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          <div className="hidden sm:flex items-center pr-4">
            <span className="text-[11px] font-mono font-semibold px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-slate-400">
              77+ Tools
            </span>
          </div>
        </div>
      </motion.div>

      {/* Interactive Dropdown Suggestions */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute left-0 right-0 top-full mt-2 bg-[#081122]/95 backdrop-blur-2xl border border-white/15 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-[380px] overflow-y-auto divide-y divide-white/5"
          >
            {/* Header / Context indicator */}
            <div className="px-4 py-2.5 bg-white/[0.02] flex items-center justify-between text-xs font-mono text-slate-400">
              <span className="flex items-center gap-1.5 text-amber-400 font-semibold uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5" />
                {searchQuery ? "Search Results" : "Popular Calculators"}
              </span>
              <span>Select to navigate</span>
            </div>

            {/* List Items */}
            <div className="p-1.5 space-y-1">
              {searchQuery ? (
                filteredTools.length > 0 ? (
                  filteredTools.map((tool) => (
                    <button
                      key={tool.id}
                      onClick={() => handleSelectTool(tool.name, tool.category)}
                      className="w-full text-left px-3.5 py-2.5 rounded-xl flex items-center justify-between hover:bg-amber-500/10 hover:border-amber-500/20 border border-transparent transition-all group cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 shrink-0">
                          <Calculator className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-white group-hover:text-amber-300 transition-colors">
                            {tool.name}
                          </div>
                          <div className="text-xs text-slate-400 line-clamp-1 font-light">
                            {tool.description}
                          </div>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-amber-400 group-hover:translate-x-1 transition-all shrink-0 ml-2" />
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-6 text-center text-slate-400 text-sm">
                    No calculators found for &quot;{searchQuery}&quot;
                  </div>
                )
              ) : (
                featuredSuggestions.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectTool(item.name, item.category)}
                    className="w-full text-left px-3.5 py-2.5 rounded-xl flex items-center justify-between hover:bg-amber-500/10 hover:border-amber-500/20 border border-transparent transition-all group cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-amber-400 group-hover:bg-amber-500/20 transition-colors shrink-0">
                        <Calculator className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white group-hover:text-amber-300 transition-colors">
                          {item.name}
                        </div>
                        <div className="text-xs text-slate-400 font-light">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-amber-400 group-hover:translate-x-1 transition-all shrink-0 ml-2" />
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
